import { useCallback, useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription } from '@quebon/ui/components/alert'
import { Button } from '@quebon/ui/components/button'
import { cn } from '@quebon/ui/lib/utils'
import { QRCodeSVG } from 'qrcode.react'
import { Camera, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react'
import { LearningContentText } from '@quebon/shared-learning/components/common/LearningContentText'
import { lcCoachingApi } from '@/api/lcCoaching'
import { deleteCoachingVideo, listCoachingVideos, retryCoachingVideo } from '@/api/lcCoachingVideos'
import { getExplainVideoCapability } from '@/api/lcExplainVideos'
import type { ExplainVideoView } from '@/api/lcExplainVideos'
import { LcEvidenceVideoPlayer } from '@/components/common/LcEvidenceVideoPlayer'
import { getApiErrorMessage } from '@/utils/apiError'
import type { ExplainPhoto } from '@/api/lcLearning'
import type {
  BuildupGrade,
  BuildupStepGradeItem,
  ComponentProgressDetailView,
  BuildupCoachingItem,
  BuildupCoachingStep,
} from '@/types/coaching'
import { isRequiredBuildupGradeItem } from '../lessonOperationStatus'
import { MarkAllCorrectButton } from '../MarkAllCorrectButton'
import { AudioResponseList } from './parts/AudioResponseList'
import { PartialDataFallback } from './parts/PartialDataFallback'
import { BuildupStepGradeToggle } from './parts/BuildupStepGradeToggle'

type SaveGradesHandler = (grades: BuildupStepGradeItem[]) => Promise<void>
type DraftGrades = Record<string, BuildupStepGradeItem>
type GradeChangeHandler = (
  step: string,
  item: BuildupCoachingItem,
  grade: BuildupGrade,
) => void

const STEP_TONES = [
  {
    surface: 'border-sky-200 border-l-sky-400 bg-sky-50/45',
    badge: 'bg-sky-100 text-sky-700 ring-sky-200',
  },
  {
    surface: 'border-amber-200 border-l-amber-400 bg-amber-50/45',
    badge: 'bg-amber-100 text-amber-700 ring-amber-200',
  },
  {
    surface: 'border-emerald-200 border-l-emerald-400 bg-emerald-50/45',
    badge: 'bg-emerald-100 text-emerald-700 ring-emerald-200',
  },
  {
    surface: 'border-rose-200 border-l-rose-400 bg-rose-50/45',
    badge: 'bg-rose-100 text-rose-700 ring-rose-200',
  },
] as const

const HINT_PREFIX_PATTERN = /^\s*["“'‘]?\s*힌트\s*[:：]\s*/
const DDAMI_PREFIX_PATTERN = /^\s*["“'‘]?\s*따미\s*[:：]\s*/
const TRAILING_QUOTE_PATTERN = /\s*["”'’]\s*$/

interface Props {
  component: ComponentProgressDetailView
  learnerId?: number
  canEdit?: boolean
  onSaveGrades?: SaveGradesHandler
  isSubmitting?: boolean
  draftResetKey?: number
  onDraftStateChange?: (state: { dirty: boolean; saving: boolean }) => void
}

export function BuildupCoachingDetail({ component, learnerId, canEdit, onSaveGrades, isSubmitting, draftResetKey, onDraftStateChange }: Props) {
  const { t } = useTranslation('coaching')
  const buildup = component.buildup
  const gradable = Boolean(canEdit && onSaveGrades)
  const persistedGradeSignature = buildup?.steps
    .flatMap((step) => step.items.map((item) => `${step.step}:${item.number}:${item.grade ?? ''}`))
    .join('|') ?? ''
  const [draftGrades, setDraftGrades] = useState<DraftGrades>({})

  const previousGradeSignature = useRef(persistedGradeSignature)
  const stateCallback = useRef(onDraftStateChange)
  stateCallback.current = onDraftStateChange
  useEffect(() => { setDraftGrades({}) }, [component.componentProgressId, draftResetKey])
  useEffect(() => {
    const signatureChanged = previousGradeSignature.current !== persistedGradeSignature
    previousGradeSignature.current = persistedGradeSignature
    if (stateCallback.current == null) {
      if (signatureChanged) setDraftGrades({})
      return
    }
    // In the workspace, refetches update untouched rows without discarding pending grades.
    setDraftGrades((current) => {
      const next = { ...current }
      let changed = false
      for (const [key, pending] of Object.entries(current)) {
        const persisted = buildup?.steps.find((step) => step.step === pending.step)?.items.find((item) => item.number === pending.itemNo)
        if (persisted?.grade === pending.grade) { delete next[key]; changed = true }
      }
      return changed ? next : current
    })
  }, [persistedGradeSignature, buildup])
  const dirty = Object.keys(draftGrades).length > 0
  const saving = isSubmitting === true
  const requiredGradeItems = buildup?.steps.flatMap((step) => step.items
    .filter((item) => isRequiredBuildupGradeItem(step.step, item.number))
    .map((item) => ({ step: step.step, item }))) ?? []
  useEffect(() => { stateCallback.current?.({ dirty, saving }) }, [dirty, saving])
  useEffect(() => () => stateCallback.current?.({ dirty: false, saving: false }), [])

  const handleGradeChange: GradeChangeHandler = (step, item, grade) => {
    const key = gradeKey(step, item.number)
    setDraftGrades((current) => {
      if (grade === item.grade) {
        const next = { ...current }
        delete next[key]
        return next
      }
      return {
        ...current,
        [key]: { step, itemNo: item.number, grade },
      }
    })
  }

  const handleMarkAllCorrect = () => {
    if (!gradable || saving || requiredGradeItems.length === 0) return
    setDraftGrades((current) => {
      const next = { ...current }
      for (const { step, item } of requiredGradeItems) {
        const key = gradeKey(step, item.number)
        if (item.grade === 'O') delete next[key]
        else next[key] = { step, itemNo: item.number, grade: 'O' }
      }
      return next
    })
  }

  const pendingGrades = Object.values(draftGrades)
  const unavailableGrades = onDraftStateChange ? pendingGrades.filter((pending) => !buildup?.steps.some((step) => step.step === pending.step && step.items.some((item) => item.number === pending.itemNo))) : []
  const hasUnavailableGrades = unavailableGrades.length > 0
  const missingGradeCount = buildup?.steps.reduce(
    (count, step) => count + step.items.filter((item) => (
      isRequiredBuildupGradeItem(step.step, item.number)
      && !(draftGrades[gradeKey(step.step, item.number)]?.grade ?? item.grade)
    )).length,
    0,
  ) ?? 0
  const hasMissingGrades = missingGradeCount > 0
  const gradeStatusText = hasMissingGrades
    ? t('buildup.grade.missingCount', { count: missingGradeCount })
    : pendingGrades.length > 0
      ? t('buildup.grade.pendingCount', { count: pendingGrades.length })
      : t('buildup.grade.noChanges')
  const gradeHintText = hasMissingGrades
    ? t('buildup.grade.completeAllHint')
    : t('buildup.grade.saveHint')
  const handleSaveGrades = async () => {
    if (!onSaveGrades || pendingGrades.length === 0 || hasMissingGrades || hasUnavailableGrades || isSubmitting) return
    try {
      await onSaveGrades(pendingGrades)
      setDraftGrades({})
    } catch {
      // Mutation layer owns the localized error toast. Keep drafts so the teacher can retry.
      return
    }
  }

  if (!buildup) return <PartialDataFallback type="buildup" />

  return (
    <div className="space-y-3">
      <section
        aria-label={t('buildup.workbook.title')}
        className="rounded-lg border border-gray-200 bg-white p-3"
      >
        {gradable && requiredGradeItems.length > 0 ? (
          <div className="mb-3 flex justify-end">
            <MarkAllCorrectButton onClick={handleMarkAllCorrect} disabled={saving} />
          </div>
        ) : null}
        <div className="space-y-3">
          {buildup.steps.map((step, index) => {
            const isStep4 = stepNumber(step.step, index) === '4'

            return (
              <StepBlock
                key={step.step}
                step={step}
                index={index}
                gradable={gradable}
                draftGrades={draftGrades}
                onGradeChange={handleGradeChange}
                isSubmitting={isSubmitting}
                extendedContent={isStep4 ? (
                  <>
                    <BuildupResponsePanel
                      audioResponse1Url={buildup.audioResponse1Url}
                      audioResponse2Url={buildup.audioResponse2Url}
                      photos={buildup.photos ?? []}
                    />
                    {learnerId != null && component.componentProgressId != null && (
                      <BuildupPhotoManager
                        key={`${learnerId}:${component.componentProgressId}`}
                        learnerId={learnerId}
                        componentProgressId={component.componentProgressId}
                        initialVideos={buildup.videos ?? []}
                      />
                    )}
                  </>
                ) : undefined}
              />
            )
          })}
        </div>
      </section>

      {gradable && hasUnavailableGrades ? <Alert variant="destructive"><AlertDescription className="flex flex-col gap-2">
        <p>{t('handheldGrading.staleTarget')}</p>
        <Button type="button" variant="outline" className="min-h-11 self-start" disabled={isSubmitting} onClick={() => setDraftGrades((current) => {
          const next = { ...current }
          for (const pending of unavailableGrades) delete next[gradeKey(pending.step, pending.itemNo)]
          return next
        })}>{t('handheldGrading.discardUnavailable')}</Button>
      </AlertDescription></Alert> : null}
      {gradable && (
        <div
          data-testid="buildup-final-save"
          className={cn('flex flex-wrap items-center justify-between gap-3 px-3 py-3', onDraftStateChange ? 'sticky bottom-0 border-t border-border bg-background' : 'rounded-lg border border-gray-300 bg-white shadow-sm')}
        >
          <div>
            <p className="text-sm font-semibold text-gray-800">{gradeStatusText}</p>
            <p className="text-xs text-gray-500">{gradeHintText}</p>
          </div>
          {onDraftStateChange ? <Button type="button" className="min-h-11" onClick={() => void handleSaveGrades()}
            disabled={pendingGrades.length === 0 || hasMissingGrades || hasUnavailableGrades || isSubmitting}>
            {isSubmitting ? t('buildup.grade.saving') : t('buildup.grade.save')}
          </Button> : (
          <button
            type="button"
            onClick={handleSaveGrades}
            disabled={pendingGrades.length === 0 || hasMissingGrades || hasUnavailableGrades || isSubmitting}
            className="inline-flex min-w-24 items-center justify-center rounded-md bg-gray-900 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-gray-700 disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            {isSubmitting ? t('buildup.grade.saving') : t('buildup.grade.save')}
          </button>
          )}
        </div>
      )}
    </div>
  )
}

function BuildupPhotoManager({
  learnerId,
  componentProgressId,
  initialVideos,
}: {
  learnerId: number
  componentProgressId: number
  initialVideos: ExplainVideoView[]
}) {
  const { t } = useTranslation('coaching')
  const [expanded, setExpanded] = useState(false)
  const [uploadUrl, setUploadUrl] = useState<string | null>(null)
  const [photos, setPhotos] = useState<ExplainPhoto[]>([])
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [videos, setVideos] = useState(initialVideos)
  const [videoEnabled, setVideoEnabled] = useState(false)
  const [videoError, setVideoError] = useState<string | null>(null)
  const [busyVideoId, setBusyVideoId] = useState<string | null>(null)

  const loadVideos = useCallback(async () => {
    const next = await listCoachingVideos(learnerId, componentProgressId)
    setVideos(next)
    return next
  }, [learnerId, componentProgressId])

  const loadPhotos = useCallback(async () => {
    setIsRefreshing(true)
    try {
      const [photoResult, videoResult] = await Promise.allSettled([
        lcCoachingApi.listBuildupExplainPhotos(learnerId, componentProgressId),
        videoEnabled || initialVideos.length > 0 ? loadVideos() : Promise.resolve(null),
      ])
      if (photoResult.status === 'fulfilled') setPhotos(photoResult.value)
      if (videoResult.status === 'rejected') setVideoError(getApiErrorMessage(videoResult.reason, t))
    } finally {
      setIsRefreshing(false)
    }
  }, [learnerId, componentProgressId, videoEnabled, initialVideos.length, loadVideos, t])

  useEffect(() => {
    let cancelled = false
    lcCoachingApi.issueBuildupExplainPhotoToken(learnerId, componentProgressId)
      .then(async (r) => {
        if (cancelled) return
        setUploadUrl(`${window.location.origin}/photo-upload?token=${r.token}`)
        try {
          const capability = await getExplainVideoCapability(r.token)
          if (!cancelled) setVideoEnabled(capability.enabled)
        } catch {
          // Photo QR is still usable when the optional video capability is unavailable.
          if (!cancelled) setVideoEnabled(false)
        }
      })
      .catch(() => {
        if (!cancelled) setUploadUrl(null)
      })

    return () => {
      cancelled = true
    }
  }, [learnerId, componentProgressId])

  useEffect(() => {
    void loadPhotos()
  }, [loadPhotos])

  const hasPendingVideo = videos.some(video => ['RESERVED', 'UPLOADING', 'PROCESSING'].includes(video.status))
  useEffect(() => {
    if (!hasPendingVideo) return
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'hidden') return
      void loadVideos().catch(error => setVideoError(getApiErrorMessage(error, t)))
    }, 5000)
    return () => window.clearInterval(timer)
  }, [hasPendingVideo, loadVideos, t])

  const changeVideo = async (video: ExplainVideoView, action: 'retry' | 'delete') => {
    if (busyVideoId) return
    if (action === 'delete' && !window.confirm(t('buildup.video.deleteConfirm'))) return
    setBusyVideoId(video.id)
    setVideoError(null)
    try {
      if (action === 'retry') await retryCoachingVideo(learnerId, componentProgressId, video.id)
      else await deleteCoachingVideo(learnerId, componentProgressId, video.id)
      await loadVideos()
    } catch (error) {
      setVideoError(getApiErrorMessage(error, t))
    } finally {
      setBusyVideoId(null)
    }
  }

  const mediaKey = videoEnabled ? 'buildup.media' : 'buildup.photo'

  return (
    <section
      aria-label={t(`${mediaKey}.title`)}
      className="rounded-lg border border-gray-200 bg-white p-2"
    >
      <button
        type="button"
        aria-label={expanded ? t(`${mediaKey}.closeQr`) : t(`${mediaKey}.openQr`)}
        aria-expanded={expanded}
        className="flex w-full items-center justify-between gap-3 rounded-md px-2 py-1.5 text-left hover:bg-gray-50"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="inline-flex min-w-0 items-center gap-2">
          <Camera size={16} className="shrink-0 text-gray-500" aria-hidden="true" />
          <span className="min-w-0">
            <span className="block text-sm font-semibold text-gray-900">
              {expanded ? t(`${mediaKey}.closeQr`) : t(`${mediaKey}.openQr`)}
            </span>
            <span className="block truncate text-xs text-gray-500">{t(`${mediaKey}.hintShort`)}</span>
          </span>
        </span>
        {expanded ? (
          <ChevronUp size={16} className="shrink-0 text-gray-500" aria-hidden="true" />
        ) : (
          <ChevronDown size={16} className="shrink-0 text-gray-500" aria-hidden="true" />
        )}
      </button>

      {expanded && (
        <div className="mt-3 border-t border-gray-100 px-2 pt-3">
          <div className="flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{t(`${mediaKey}.title`)}</h3>
              <p className="mt-0.5 text-xs leading-relaxed text-gray-600">{t(`${mediaKey}.hint`)}</p>
            </div>
            <button
              type="button"
              onClick={() => void loadPhotos()}
              disabled={isRefreshing}
              className="inline-flex min-h-8 items-center gap-1.5 rounded-md border border-gray-300 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-wait disabled:text-gray-400"
            >
              <RefreshCw
                size={14}
                className={isRefreshing ? 'animate-spin' : undefined}
                aria-hidden="true"
              />
              {isRefreshing ? t('buildup.photo.refreshing') : t(`${mediaKey}.refresh`)}
            </button>
          </div>

          <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start">
            <div className="rounded-md border border-gray-200 bg-white p-2">
              {uploadUrl ? (
                <QRCodeSVG value={uploadUrl} size={136} aria-label={t(`${mediaKey}.qrAlt`)} />
              ) : (
                <div className="flex h-[136px] w-[136px] items-center justify-center text-xs text-gray-500">
                  {t('buildup.photo.qrLoading')}
                </div>
              )}
            </div>

            <PhotoThumbs photos={photos} />
          </div>
        </div>
      )}
      {videoEnabled || videos.length > 0 ? (
        <div className="mt-3 space-y-3 border-t border-border pt-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h3 className="text-sm font-semibold text-foreground">{t('buildup.video.title', { count: videos.length, max: 3 })}</h3>
            <Button type="button" variant="outline" size="sm" onClick={() => void loadVideos().catch(error => setVideoError(getApiErrorMessage(error, t)))}>
              <RefreshCw className="size-4" aria-hidden="true" />{t('buildup.media.refresh')}
            </Button>
          </div>
          {videos.length === 0 ? <p className="text-xs text-muted-foreground">{t('buildup.video.empty')}</p> : null}
          <div className="grid gap-3 sm:grid-cols-2">
            {videos.map((video, index) => (
              <div key={video.id} className="space-y-2 rounded-lg border border-border bg-card p-3">
                <p className="text-sm font-medium">{t('buildup.video.label', { index: index + 1 })}</p>
                {video.status === 'READY' && video.playbackUrl ? (
                  <LcEvidenceVideoPlayer
                    url={video.playbackUrl}
                    label={t('buildup.video.label', { index: index + 1 })}
                    onRefreshUrl={async () => (await loadVideos()).find(item => item.id === video.id)?.playbackUrl ?? undefined}
                  />
                ) : <p className={video.status === 'FAILED' ? 'text-sm text-destructive' : 'text-sm text-muted-foreground'} role="status">
                  {t(video.status === 'READY' ? 'growthAlert.videoUnavailable' : `buildup.video.status.${video.status}`)}
                </p>}
                <div className="flex gap-2">
                  {video.status === 'FAILED' ? <Button type="button" variant="outline" size="sm" disabled={busyVideoId !== null} onClick={() => void changeVideo(video, 'retry')}>{t('buildup.video.retry')}</Button> : null}
                  <Button type="button" variant="outline" size="sm" disabled={busyVideoId !== null} onClick={() => void changeVideo(video, 'delete')}>{t('buildup.video.delete')}</Button>
                </div>
              </div>
            ))}
          </div>
          {videoError ? <p role="alert" className="text-sm text-destructive">{videoError}</p> : null}
        </div>
      ) : null}
    </section>
  )
}

function StepBlock({
  step,
  index,
  gradable,
  draftGrades,
  onGradeChange,
  isSubmitting,
  extendedContent,
}: {
  step: BuildupCoachingStep
  index: number
  gradable: boolean
  draftGrades: DraftGrades
  onGradeChange: GradeChangeHandler
  isSubmitting?: boolean
  extendedContent?: ReactNode
}) {
  const { t } = useTranslation('coaching')
  const stepNo = stepNumber(step.step, index)
  const isStep4 = stepNo === '4'
  const tone = STEP_TONES[(Number(stepNo) - 1) % STEP_TONES.length] ?? STEP_TONES[0]
  const promptItem = isStep4
    ? step.items.find((item) => item.number === 1) ?? step.items[0]
    : undefined
  const modelAnswerItem = isStep4
    ? step.items.find((item) => item.number === 2)
    : undefined

  return (
    <article
      data-testid={`buildup-step-${stepNo}`}
      className={`rounded-lg border border-l-4 p-3 ${tone.surface}`}
    >
      <header className="mb-2 flex items-center gap-2">
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ring-1 ring-inset ${tone.badge}`}>
          {t('buildup.workbook.stepLabel', { n: stepNo })}
        </span>
        <h4 className="text-sm font-semibold text-gray-900">
          <LearningContentText text={step.title} />
        </h4>
      </header>

      <div
        data-testid={`buildup-step-items-${step.step}`}
        className={isStep4 ? 'grid gap-2' : 'grid gap-2 sm:grid-cols-2'}
      >
        {isStep4 && promptItem ? (
          <StepFourItem
            promptItem={promptItem}
            modelAnswerItem={modelAnswerItem}
            stepLabel={step.step}
            gradable={gradable}
            draftGrades={draftGrades}
            onGradeChange={onGradeChange}
            isSubmitting={isSubmitting}
          />
        ) : (
          step.items.map((item) => (
            <StepItem
              key={item.number}
              item={item}
              stepLabel={step.step}
              gradable={gradable}
              draftGrades={draftGrades}
              onGradeChange={onGradeChange}
              isSubmitting={isSubmitting}
            />
          ))
        )}
      </div>

      {extendedContent ? (
        <div data-testid={`buildup-step-extended-${stepNo}`} className="mt-3 space-y-3">
          {extendedContent}
        </div>
      ) : null}
    </article>
  )
}

function StepItem({
  item,
  stepLabel,
  gradable,
  draftGrades,
  onGradeChange,
  isSubmitting,
}: {
  item: BuildupCoachingItem
  stepLabel: string
  gradable: boolean
  draftGrades: DraftGrades
  onGradeChange: GradeChangeHandler
  isSubmitting?: boolean
}) {
  const { t } = useTranslation('coaching')
  const stepNo = stepLabel.match(/\d+/)?.[0]
  const hasDescriptionImages = stepNo === '2' && item.descImageUrls.length > 0
  const draftGrade = draftGrades[gradeKey(stepLabel, item.number)]?.grade
  const selectedGrade = draftGrade ?? item.grade
  const promptRow: ReferenceRowData = {
    kind: 'prompt',
    label: stepNo === '1' ? t('buildup.reference.hint') : t('buildup.reference.prompt'),
    value: stepNo === '1' ? stripReferencePrefix(item.title, HINT_PREFIX_PATTERN) : item.title,
  }
  const answerRow: ReferenceRowData = {
    kind: 'answer',
    label: t('buildup.reference.answer'),
    value: item.answer,
    tone: stepNo === '3' ? 'answer-large' : 'answer',
  }
  const explanationRow: ReferenceRowData = {
    kind: 'explanation',
    label: t('buildup.reference.explanation'),
    value: item.desc,
  }
  const criteriaRow: ReferenceRowData = {
    kind: 'criteria',
    label: t('buildup.reference.criteria'),
    value: item.answerCriteria,
  }
  const teacherCheckRow: ReferenceRowData = {
    kind: 'teacherCheck',
    label: t('buildup.label.teacherCheck'),
    value: item.teacherCheck,
  }
  const itemRows: ReferenceRowData[] = stepNo === '1'
    ? [promptRow, answerRow]
    : stepNo === '2' || stepNo === '3'
      ? [promptRow, answerRow, explanationRow, criteriaRow, teacherCheckRow]
      : [
          promptRow,
          { kind: 'options', label: t('buildup.label.options'), value: item.options },
          answerRow,
          explanationRow,
          criteriaRow,
          teacherCheckRow,
        ]

  return (
    <div
      data-testid="buildup-step-item"
      className={[
        'rounded-md border border-gray-200/90 bg-white/85 p-3 shadow-[0_1px_2px_rgba(15,23,42,0.03)]',
        hasDescriptionImages
          ? 'sm:col-span-2 sm:grid sm:grid-cols-[minmax(0,1fr)_minmax(180px,1fr)] sm:items-start sm:gap-4'
          : '',
      ].filter(Boolean).join(' ')}
    >
      <div data-testid={`buildup-item-content-${item.number}`} className="min-w-0">
        <ItemNumberHeader item={item} />

        <div className="space-y-1.5 rounded-md border border-gray-200 bg-white p-2">
          {itemRows.map((row) => (
            <ReferenceRow
              key={row.kind}
              label={row.label}
              value={displayReferenceValue(row.value)}
              tone={row.tone}
            />
          ))}
        </div>

        {gradable && (
          <GradeControl
            value={selectedGrade}
            showSelfBadge={draftGrade == null && item.gradedBy === 'SELF'}
            onChange={(grade) => onGradeChange(stepLabel, item, grade)}
            isSubmitting={isSubmitting}
            hidePartial={stepNo === '1'}
          />
        )}
      </div>

      {hasDescriptionImages && (
        <DescriptionImages item={item} />
      )}
    </div>
  )
}

function StepFourItem({
  promptItem,
  modelAnswerItem,
  stepLabel,
  gradable,
  draftGrades,
  onGradeChange,
  isSubmitting,
}: {
  promptItem: BuildupCoachingItem
  modelAnswerItem?: BuildupCoachingItem
  stepLabel: string
  gradable: boolean
  draftGrades: DraftGrades
  onGradeChange: GradeChangeHandler
  isSubmitting?: boolean
}) {
  const { t } = useTranslation('coaching')
  const draftGrade = draftGrades[gradeKey(stepLabel, promptItem.number)]?.grade
  const selectedGrade = draftGrade ?? promptItem.grade
  const modelAnswer = firstReferenceValue(modelAnswerItem?.answer, promptItem.answer)
  const explanation = firstReferenceValue(modelAnswerItem?.desc, promptItem.desc)
  const answerCriteria = firstReferenceValue(
    modelAnswerItem?.answerCriteria,
    promptItem.answerCriteria,
  )
  const teacherCheck = firstReferenceValue(modelAnswerItem?.teacherCheck, promptItem.teacherCheck)

  return (
    <div
      data-testid="buildup-step-item"
      className="rounded-md border border-gray-200/90 bg-white/85 p-3 shadow-[0_1px_2px_rgba(15,23,42,0.03)]"
    >
      <div>
        <NumberedReference
          item={promptItem}
          rows={[
            {
              kind: 'prompt',
              label: t('buildup.reference.prompt'),
              value: stripReferencePrefix(promptItem.title, DDAMI_PREFIX_PATTERN),
            },
            {
              kind: 'answer',
              label: t('buildup.reference.modelAnswer'),
              value: modelAnswer,
              tone: 'answer',
            },
            {
              kind: 'explanation',
              label: t('buildup.reference.explanation'),
              value: explanation,
            },
            {
              kind: 'criteria',
              label: t('buildup.reference.criteria'),
              value: answerCriteria,
            },
            {
              kind: 'teacherCheck',
              label: t('buildup.label.teacherCheck'),
              value: teacherCheck,
            },
          ]}
        />
      </div>

      {gradable && (
        <GradeControl
          value={selectedGrade}
          showSelfBadge={draftGrade == null && promptItem.gradedBy === 'SELF'}
          onChange={(grade) => onGradeChange(stepLabel, promptItem, grade)}
          isSubmitting={isSubmitting}
        />
      )}
    </div>
  )
}

interface ReferenceRowData {
  kind: 'prompt' | 'options' | 'answer' | 'explanation' | 'criteria' | 'teacherCheck'
  label: string
  value: string | null
  tone?: 'answer' | 'answer-large'
}

function NumberedReference({
  item,
  fallbackNumber,
  rows,
}: {
  item?: BuildupCoachingItem
  fallbackNumber?: number
  rows: ReferenceRowData[]
}) {
  return (
    <section
      data-testid={`buildup-numbered-reference-${item?.number ?? fallbackNumber}`}
      className="rounded-md border border-gray-200 bg-white p-2"
    >
      <ItemNumberHeader item={item} fallbackNumber={fallbackNumber} />
      <div className="space-y-1.5">
        {rows.map((row) => (
          <ReferenceRow
            key={row.kind}
            label={row.label}
            value={displayReferenceValue(row.value)}
            tone={row.tone}
          />
        ))}
      </div>
    </section>
  )
}

function ItemNumberHeader({
  item,
  fallbackNumber,
}: {
  item?: BuildupCoachingItem
  fallbackNumber?: number
}) {
  const number = item?.number ?? fallbackNumber

  return (
    <div
      data-testid={`buildup-item-header-${number}`}
      className="mb-2 min-h-6"
    >
      <span className="text-xs font-semibold text-gray-400">#{number}</span>
    </div>
  )
}

function DescriptionImages({ item }: { item: BuildupCoachingItem }) {
  const { t } = useTranslation('coaching')

  return (
    <div
      data-testid={`buildup-item-images-${item.number}`}
      className="mt-3 flex min-w-0 flex-col gap-2 sm:mt-0"
    >
      {item.descImageUrls.map((url, index) => (
        <a
          key={`${url}-${index}`}
          href={url}
          target="_blank"
          rel="noreferrer"
          className="block"
        >
          <img
            src={url}
            alt={t('buildup.reference.imageAlt', { number: item.number, index: index + 1 })}
            className="max-h-48 w-full rounded-md border border-gray-200 bg-white object-contain"
          />
        </a>
      ))}
    </div>
  )
}

function GradeControl({
  value,
  showSelfBadge,
  onChange,
  isSubmitting,
  hidePartial,
}: {
  value: BuildupGrade | null | undefined
  showSelfBadge: boolean
  onChange: (grade: BuildupGrade) => void
  isSubmitting?: boolean
  hidePartial?: boolean
}) {
  const { t } = useTranslation('coaching')

  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <span className="text-xs font-semibold text-gray-500">{t('buildup.grade.label')}</span>
      <BuildupStepGradeToggle
        value={value}
        disabled={isSubmitting}
        onChange={onChange}
        hidePartial={hidePartial}
      />
      {showSelfBadge && (
        <span
          data-testid="graded-by-self"
          className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
        >
          {t('buildup.grade.bySelf')}
        </span>
      )}
    </div>
  )
}

function gradeKey(step: string, itemNo: number): string {
  return `${step}:${itemNo}`
}

function ReferenceRow({
  label,
  value,
  tone,
}: {
  label: string
  value: string
  tone?: 'answer' | 'answer-large'
}) {
  const valueClassName = tone === 'answer-large'
    ? 'whitespace-pre-wrap text-2xl leading-8 font-bold text-red-600'
    : tone === 'answer'
      ? 'whitespace-pre-wrap text-lg leading-7 font-bold text-red-600'
      : 'whitespace-pre-wrap text-sm leading-6 text-gray-800'

  return (
    <div className="grid gap-1 text-xs leading-relaxed sm:grid-cols-[72px_minmax(0,1fr)]">
      <span className="font-semibold text-gray-500">{label}</span>
      <span className={valueClassName}>
        <LearningContentText text={value} />
      </span>
    </div>
  )
}

function BuildupResponsePanel({
  audioResponse1Url,
  audioResponse2Url,
  photos,
}: {
  audioResponse1Url: string | null
  audioResponse2Url: string | null
  photos: { id: number; photoUrl: string }[]
}) {
  const { t } = useTranslation('coaching')

  return (
    <div className="grid gap-2 lg:grid-cols-[minmax(0,1fr)_220px]">
      <section className="rounded-md border border-gray-200 bg-gray-50/60 p-3">
        <header className="mb-2 text-xs font-semibold text-gray-600">
          {t('buildup.workbook.audioTitle')}
        </header>
        <AudioResponseList response1={audioResponse1Url} response2={audioResponse2Url} />
      </section>

      <section className="rounded-md border border-gray-200 bg-gray-50/60 p-3">
        <header className="mb-2 text-xs font-semibold text-gray-600">
          {t('buildup.photos', { count: photos.length })}
        </header>
        <PhotoThumbs photos={photos} />
      </section>
    </div>
  )
}

function PhotoThumbs({ photos }: { photos: { id: number; photoUrl: string }[] }) {
  const { t } = useTranslation('coaching')
  if (photos.length === 0) {
    return <p className="text-xs text-gray-400">{t('buildup.photo.empty')}</p>
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {photos.map((p) => (
        <a key={p.id} href={p.photoUrl} target="_blank" rel="noreferrer">
          <img
            src={p.photoUrl}
            alt=""
            className="h-16 w-16 rounded border border-gray-200 object-cover hover:opacity-80"
          />
        </a>
      ))}
    </div>
  )
}

function stepNumber(step: string, index: number): string {
  return step.match(/\d+/)?.[0] ?? String(index + 1)
}

function displayReferenceValue(value: string | null): string {
  const trimmed = value?.trim()
  return trimmed && trimmed.length > 0 ? trimmed : '-'
}

function hasReferenceValue(value: string | null | undefined): value is string {
  return Boolean(value?.trim())
}

function firstReferenceValue(...values: (string | null | undefined)[]): string | null {
  return values.find(hasReferenceValue) ?? null
}

function stripReferencePrefix(value: string | null, pattern: RegExp): string | null {
  if (!value || !pattern.test(value)) return value
  return value.replace(pattern, '').replace(TRAILING_QUOTE_PATTERN, '').trim()
}
